/**
 * Fichas de evidencia (`/api/fichas`).
 *
 * Tres tipos con reglas distintas que comparten endpoint: documental,
 * estadística y de opinión. La nota de una ficha cuantitativa es un snapshot que
 * el backend recalcula en cada cambio; si el cálculo deja de ser posible, se
 * limpia en vez de conservarse.
 *
 * Es la única dependencia que bloquea el borrado de un instrumento, por su valor
 * probatorio en auditoría.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type { Ficha } from '@/interfaces/Ficha'
import { http, getAuthToken } from '@/services/http'

/**
 * Crea fichas · petición `POST`.
 *
 * @returns { ok: boolean; data?: Ficha; error?: string }
 */
export async function crearFichaService(
  url: string,
  payload: Omit<Ficha, 'id_ficha'>
): Promise<{ ok: boolean; data?: Ficha; error?: string }> {
  const result = await http.post<any>(url, payload)
  if (!result.ok) {
    const msg = Object.values(result.errors ?? {}).flat()[0] ?? `Error HTTP ${result.status}`
    return { ok: false, error: msg }
  }
  const d = result.data
  return { ok: true, data: (d?.data ?? d) as Ficha }
}

/**
 * Consulta fichas · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Ficha | null
 */
export async function getFichaByIdService(url: string, id: number): Promise<Ficha | null> {
  const result = await http.get<any>(`${url.replace(/\/$/, '')}/${id}`)
  if (!result.ok) return null
  const d = result.data
  return (d?.data ?? d ?? null) as Ficha | null
}

/**
 * Consulta fichas · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Ficha[] | null
 */
export async function getFichasService(
  url: string,
  params?: { id_instrumento?: number; tipo?: string; estado?: string }
): Promise<Ficha[] | null> {
  const query = params ? new URLSearchParams(params as any).toString() : ''
  const endpoint = query ? `${url}?${query}` : url
  const result = await http.get<any>(endpoint)
  if (!result.ok) return null
  const d = result.data
  return Array.isArray(d) ? d : (d?.data ?? null)
}

/**
 * Actualiza fichas · petición `PUT`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Ficha | null
 */
export async function actualizarFichaService(url: string, id: number, payload: Partial<Ficha>): Promise<Ficha | null> {
  const result = await http.put<any>(`${url.replace(/\/$/, '')}/${id}`, payload)
  if (!result.ok) return null
  const d = result.data
  return (d?.data ?? d ?? null) as Ficha | null
}

// FormData — no pasa por http.ts (multipart/form-data no es JSON), se inyecta el token manualmente
export async function uploadArchivoFichaService(
  url: string,
  idFicha: number,
  archivo: File
): Promise<{ ok: boolean; documento?: string; error?: string }> {
  try {
    const formData = new FormData()
    formData.append('archivo', archivo)
    const token = getAuthToken()
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`

    const endpoint = `${url.replace(/\/$/, '')}/${idFicha}/archivo`
    const res = await fetch(endpoint, { method: 'POST', headers, body: formData })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) return { ok: false, error: (body as any).message ?? `Error HTTP ${res.status}` }
    return { ok: true, documento: (body as any).documento }
  } catch (err) {
    console.error('Error al subir archivo:', err)
    return { ok: false, error: 'Error de red al subir archivo' }
  }
}

/**
 * Elimina fichas · petición `DELETE`.
 *
 * No lanza: `true` solo si la petición tuvo éxito. `false` agrupa todos los
 * fallos posibles sin distinguirlos.
 *
 * @returns boolean
 */
export async function eliminarFichaService(url: string, id: number): Promise<boolean> {
  const result = await http.delete(`${url.replace(/\/$/, '')}/${id}`)
  return result.ok
}

// ── Ficha cuantitativa: cálculo de la nota ──────────────────────────

/** Valoración de un periodo (año, semestre, cohorte…) que crea el calificador. */
export interface ValoracionPeriodo {
  id_valoracion: number
  id_ficha: number
  periodo: string
  valoracion: string
  observacion?: string | null
  orden: number
}

/** Etiqueta elegible con los puntos que vale, derivados de la rúbrica. */
export interface EtiquetaValoracion {
  valoracion: string
  puntos: number
  tendencia?: string | null
  grado?: string | null
}

/** Banda de la rúbrica en la que cae la nota. */
export interface TendenciaCuantitativa {
  tendencia?: string | null
  grado?: string | null
  descripcion?: string | null
  rango_min: number
  rango_max: number
  pct_min: number
  pct_max: number
}

/** Forma con la que el API de fichas devuelve este dato. */
export interface DesgloseCuantitativo {
  metodo: string
  porcentaje_cumplimiento: number
  porcentaje_sin_tope: number
  topado_al_100: boolean
  escala_max: number
  tendencia?: TendenciaCuantitativa | null
  // Método por periodos
  periodos?: Array<{ periodo: string; valoracion: string; puntos: number }>
  periodos_considerados?: number
  puntaje_maximo_periodo?: number
  puntaje_total?: number
  puntaje_apreciado?: number
  valoraciones_no_reconocidas?: string[]
  // Método directo
  logro?: number
  meta?: number
}

/** Forma con la que el API de fichas devuelve este dato. */
export interface NotaCuantitativa {
  nota: number | null
  motivo: string | null
  desglose: DesgloseCuantitativo | null
}

/** Forma con la que el API de fichas devuelve este dato. */
export interface PeriodosDeFicha {
  id_ficha: number
  metodo_calculo: string | null
  logro: number | null
  meta: number | null
  periodos: ValoracionPeriodo[]
  nota_sugerida: NotaCuantitativa
  etiquetas: EtiquetaValoracion[]
}

/**
 * Consulta fichas · `GET /api/fichas`.
 *
 * @returns PeriodosDeFicha | null
 */
export async function getPeriodosService(idFicha: number): Promise<PeriodosDeFicha | null> {
  const r = await http.get<PeriodosDeFicha>(`/api/fichas/${idFicha}/periodos`)
  return r.ok ? (r.data ?? null) : null
}

/**
 * Crea fichas · `POST /api/fichas`.
 *
 * @returns boolean
 */
export async function crearPeriodoService(
  idFicha: number,
  payload: { periodo: string; valoracion: string; observacion?: string | null }
): Promise<boolean> {
  const r = await http.post(`/api/fichas/${idFicha}/periodos`, payload)
  return r.ok
}

/**
 * Actualiza fichas · `PUT /api/periodos`.
 *
 * @returns boolean
 */
export async function actualizarPeriodoService(
  id: number,
  payload: Partial<Pick<ValoracionPeriodo, 'periodo' | 'valoracion' | 'observacion' | 'orden'>>
): Promise<boolean> {
  const r = await http.put(`/api/periodos/${id}`, payload)
  return r.ok
}

/**
 * Elimina fichas · `DELETE /api/periodos`.
 *
 * @returns boolean
 */
export async function eliminarPeriodoService(id: number): Promise<boolean> {
  const r = await http.delete(`/api/periodos/${id}`)
  return r.ok
}

/** Periodo aún no guardado: el formulario los arma antes de crear la ficha. */
export interface PeriodoNuevo {
  periodo: string
  valoracion: string
  observacion?: string | null
}

/**
 * Valoraciones elegibles, sin depender de una ficha.
 *
 * El formulario de creación las necesita ANTES de que la ficha exista; hasta
 * ahora solo se podían obtener pidiendo el cálculo de una ficha ya guardada, y
 * de ahí salía el flujo en dos pasos.
 */
export async function getEtiquetasValoracionService(): Promise<EtiquetaValoracion[]> {
  const r = await http.get<EtiquetaValoracion[]>('/api/valoracion-cuantitativa/etiquetas')
  return r.ok ? (r.data ?? []) : []
}

/**
 * Nota que daría un cálculo todavía sin guardar.
 *
 * La calcula el backend a propósito: reimplementar la fórmula aquí es lo que
 * hace que la nota que se muestra y la que se guarda acaben discrepando.
 */
export async function simularNotaCuantitativaService(payload: {
  metodo_calculo: 'por_periodos' | 'directo'
  logro?: number | null
  meta?: number | null
  escala_max?: number | null
  periodos?: PeriodoNuevo[]
}): Promise<NotaCuantitativa | null> {
  const r = await http.post<NotaCuantitativa>('/api/valoracion-cuantitativa/simular', payload)
  return r.ok ? (r.data ?? null) : null
}
